import { getUploadPolicy, uploadFileToS3, createContent } from '../api/contentApi';
import { getMediaDimensions, isImageFile, isVideoFile } from './mediaValidation';

const MAX_CONCURRENCY = 3;

/**
 * Detect media type from a File object.
 */
function detectMediaType(file) {
  if (isVideoFile(file)) return 'video';
  if (isImageFile(file)) return 'image';
  return 'image';
}

/**
 * Upload multiple files in parallel batches.
 *
 * Each entry in `files` is an object `{ id, file, title, dims?, extra? }`:
 *   - id:    stable client-side id (returned in callbacks so the caller can
 *            map progress back to its pendingFiles state regardless of order)
 *   - file:  the raw File object
 *   - title: caller-provided title (falls back to filename stem if blank)
 *   - dims:  optional pre-computed { width, height } — skips re-measuring
 *   - extra: optional extra fields merged into the createContent payload
 *            (e.g. is_permanent, valid_from, valid_until)
 *
 * @param {Object} params
 * @param {Array<{id: string, file: File, title?: string, dims?: {width:number,height:number}, extra?: object}>} params.files
 * @param {string} params.projectId
 * @param {string} params.section
 * @param {string} [params.targetScreenId] - Target screen for aspect ratio validation + processing
 * @param {Object} [params.callbacks]
 * @param {function} [params.callbacks.onFileStart]        - (entry, index) => void
 * @param {function} [params.callbacks.onFileStatusChange] - (entry, index, status, meta?) => void  status in 'uploading'|'done'|'failed'
 * @param {function} [params.callbacks.onFileComplete]     - (entry, index, result) => void
 * @param {function} [params.callbacks.onFileError]        - (entry, index, error) => void
 * @param {function} [params.callbacks.onProgress]         - (completed, total) => void
 * @returns {Promise<{succeeded: number, failed: number, results: Array}>}
 */
export async function uploadBatch({ files, projectId, section, targetScreenId, callbacks = {} }) {
  const { onFileStart, onFileStatusChange, onFileComplete, onFileError, onProgress } = callbacks;
  const results = [];
  let completed = 0;

  // Process files in parallel with concurrency limit
  for (let i = 0; i < files.length; i += MAX_CONCURRENCY) {
    const batch = files.slice(i, i + MAX_CONCURRENCY);

    const batchResults = await Promise.allSettled(
      batch.map(async (entry, batchIdx) => {
        const fileIndex = i + batchIdx;
        const { file, title, dims: preComputedDims, extra } = entry;
        onFileStart?.(entry, fileIndex);
        onFileStatusChange?.(entry, fileIndex, 'uploading');

        // Use pre-computed dimensions if provided, otherwise measure now
        let dims = preComputedDims || null;
        if (!dims) {
          try {
            dims = await getMediaDimensions(file);
          } catch { /* non-media file, skip dims */ }
        }

        // Step 1: Get upload policy
        const policy = await getUploadPolicy(file.name, projectId, section);

        // Step 2: Upload to S3
        await uploadFileToS3(file, policy);

        // Step 3: Save metadata
        const resolvedTitle = (title || '').trim() || file.name.replace(/\.[^/.]+$/, '');
        const contentData = {
          contentId: policy.contentId,
          filePath: policy.filePath,
          projectId,
          section,
          fileName: policy.fileName || file.name,
          type: detectMediaType(file),
          title: resolvedTitle,
          description: '',
          tags: [],
          ...(extra || {}),
        };

        if (dims) {
          contentData.width = dims.width;
          contentData.height = dims.height;
          // Video duration — without it the backend caps looped videos at 15s.
          if (dims.duration) contentData.duration = dims.duration;
        }
        if (targetScreenId) {
          contentData.target_screen_id = targetScreenId;
        }

        const result = await createContent(contentData);

        // The manifest keys items by SumadhuraCms.name (NOT content_id).
        // Callers that schedule content after upload need the name to
        // build a working schedule; fall back to the nanoid + filename
        // derivation for older backend responses. See
        // WebsiteContentUploader.jsx handleUpload for the consumer side.
        const savedName =
          result?.data?.name ||
          result?.name ||
          ((policy.fileName || file.name || '').replace(/\.[^/.]+$/, ''));

        completed++;
        onFileStatusChange?.(entry, fileIndex, 'done', {
          contentId: policy.contentId,
          contentName: savedName,
        });
        onFileComplete?.(entry, fileIndex, result);
        onProgress?.(completed, files.length);

        return { id: entry.id, file, result, contentId: policy.contentId, contentName: savedName };
      })
    );

    batchResults.forEach((result, batchIdx) => {
      const fileIndex = i + batchIdx;
      const entry = batch[batchIdx];
      if (result.status === 'rejected') {
        completed++;
        const errMsg = result.reason?.message || String(result.reason || 'Upload failed');
        onFileStatusChange?.(entry, fileIndex, 'failed', { error: errMsg });
        onFileError?.(entry, fileIndex, result.reason);
        onProgress?.(completed, files.length);
        results.push({ status: 'failed', id: entry.id, file: entry.file, error: errMsg });
      } else {
        results.push({ status: 'succeeded', ...result.value });
      }
    });
  }

  return {
    succeeded: results.filter(r => r.status === 'succeeded').length,
    failed: results.filter(r => r.status === 'failed').length,
    results,
  };
}
